<script lang="ts">
  import Image from '@components/image.svelte'
  import {
    foulDescription,
    getTeamIdFromNbaEvent,
    ordinalNumber,
    reviewDesctiption,
    shotTypeDescription,
    stoppageDescription,
    turnoverDescription,
    violationDescription,
    type GameraTeamReference,
    type NbaEvent,
  } from '@statmuse/core/gamera'
  import { players } from './stores'
  import { round, some } from 'lodash-es'

  export let play: NbaEvent
  export let awayTeam: GameraTeamReference | undefined
  export let homeTeam: GameraTeamReference | undefined
  export let bgColor = true

  $: team =
    awayTeam?.teamId === getTeamIdFromNbaEvent(play)
      ? awayTeam
      : homeTeam?.teamId === getTeamIdFromNbaEvent(play)
      ? homeTeam
      : undefined
</script>

<div
  class={`py-2 px-3 bg-gray-8 dark:bg-gray-3 ${
    bgColor ? 'rounded-2xl border border-gray-6 dark:border-gray-3' : ''
  }`}
  style:background={bgColor ? team?.colors?.backgroundColor : ''}
  style:color={bgColor ? team?.colors?.foregroundColor : ''}
  style:border-color={bgColor ? team?.colors?.backgroundColor : ''}
>
  <div class="flex items-center">
    {#if !bgColor && team}
      <Image
        src={team.logoImageUrl ?? ''}
        alt={team.name ?? ''}
        width={60}
        height={60}
        class="w-4 h-4 object-contain mr-2"
      />
    {/if}
    <div class="flex-1">
      {#if play.type === 'fieldGoal' || play.type === 'legacyFieldGoal'}
        {@const player = $players[play.playerId ?? 0]}
        {@const assistBy = $players[play.assistPlayerId ?? 0]}
        <p>
          {player?.shortName ?? player?.longName}
          {play.isMade ? 'makes' : 'misses'}
          {`${
            some(['layup', 'dunk', 'finger roll', 'tip'], (x) =>
              shotTypeDescription(play)?.includes(x),
            )
              ? ''
              : `${round(play.distanceFeet, 0)}' `
          }${shotTypeDescription(play)}`}
        </p>
        {#if assistBy}
          <p class="text-sm opacity-50">
            Assisted by {assistBy.shortName ?? assistBy.longName}
          </p>
        {/if}
      {:else if play.type === 'rebound'}
        {@const player = $players[play.playerId ?? 0]}
        {player?.shortName ??
          player?.longName ??
          `${team?.nickname ?? ''} team`}
        {play.reboundType === 'offensive' ? 'offensive ' : ''}rebound
      {:else if play.type === 'turnover'}
        {@const player = $players[play.turnoverPlayerId ?? 0]}
        {@const stealBy = $players[play.stealPlayerId ?? 0]}
        <p>
          {player?.shortName ?? player?.longName ?? ''}
          {turnoverDescription(play.turnoverType)}
        </p>
        {#if stealBy}
          <p class="text-sm opacity-50">
            Stolen by {stealBy.shortName ?? stealBy.longName}
          </p>
        {/if}
      {:else if play.type === 'foul'}
        {@const player = $players[play.foulPlayerId ?? 0]}
        {@const fouledPlayer = $players[play.foulDrawnPlayerId ?? 0]}
        {`${player?.shortName ?? player?.longName ?? ''} ${foulDescription(
          play.foulType,
        )} foul${
          fouledPlayer
            ? ` on ${fouledPlayer.shortName ?? fouledPlayer.longName}`
            : ''
        }`}
      {:else if play.type === 'freeThrow'}
        {@const player = $players[play.playerId ?? 0]}
        {`${player?.shortName ?? player?.longName ?? 'Player'} ${
          play.isMade ? 'makes' : 'misses'
        } free throw ${play.attemptNumber}/${play.totalGranted}`}
      {:else if play.type === 'teamTimeout'}
        {`${team?.nickname} ${play.timeoutSeconds}-second timeout`}
      {:else if play.type === 'endPeriod'}
        {`End of ${ordinalNumber(play.period)} quarter`}
      {:else if play.type === 'lineupChange'}
        {`${play.subbedInPlayerIds
          .map((id) => $players[id]?.shortName ?? $players[id]?.longName)
          .filter((x) => !!x)
          .join(', ')} enters game ${play.subbedOutPlayerIds ? 'for ' : ' '}${
          play.subbedOutPlayerIds
            ?.map((id) => $players[id]?.shortName ?? $players[id]?.longName)
            .filter((x) => !!x)
            .join(', ') ?? ''
        }`}
      {:else if play.type === 'stoppage'}
        {`Stoppage ${stoppageDescription(play.stoppageType)}`}
      {:else if play.type === 'violation'}
        {@const player = $players[play.violationPlayerId ?? 0]}
        {`${
          player?.shortName ?? player?.longName ?? 'Team'
        } ${violationDescription(play.violationType)}`}
      {:else if play.type === 'review'}
        {`Review: ${reviewDesctiption(play.reviewType)}`}
      {:else if play.type === 'challengeTimeout'}
        {`${team?.nickname ?? 'Team'} challenge timeout`}
      {:else if play.type === 'ejection'}
        {@const player = $players[play.ejectionPlayerId ?? 0]}
        {`${player?.shortName ?? player?.longName ?? ''} ejected from the game`}
      {:else if play.type === 'jumpBall' || play.type === 'openTip'}
        {@const possessionPlayer = $players[play.possessionPlayerId ?? 0]}
        {@const awayPlayer = $players[play.awayPlayerId ?? 0]}
        {@const homePlayer = $players[play.homePlayerId ?? 0]}
        {`${play.type === 'openTip' ? 'Open tip' : 'Jump ball'}.${
          awayPlayer && homePlayer
            ? ` ${awayPlayer?.shortName ?? awayPlayer?.longName} vs ${
                homePlayer?.shortName ?? homePlayer?.longName
              }.${
                possessionPlayer
                  ? ` ${
                      possessionPlayer?.shortName ?? possessionPlayer?.longName
                    } gains possesion.`
                  : ''
              }`
            : ''
        }`}
      {:else}
        {play.type}
      {/if}
    </div>
    <div class="text-sm text-right">
      <p>
        {`${
          play?.period <= 4
            ? play?.period
            : play?.period === 5
            ? ''
            : play?.period - 4
        }${play?.period <= 4 ? 'Q' : 'OT'}`}
        {play.clock ?? ''}
      </p>
      {#if play.awayPoints && play.homePoints}
        <p class={bgColor ? 'opacity-50' : 'text-gray-5'}>
          {play.awayPoints}-{play.homePoints}
        </p>
      {/if}
    </div>
  </div>
</div>
